/**
 * Shared wiring for a module character: its quote/run tool, the server
 * activation gate and the inactive notice. Used by the module «دستیار» tab
 * and by the yar card on each module hub page.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, V2Button } from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import { apiGet, apiPut } from '../../lib/apiClient.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { isAiLive } from '../../routes/ai/connect/mode.ts';
import { useYarAccess } from './YarAccess.js';
import { MODULE_YAR_MAP, yarForModule, type ModuleYarWiring, type YarDef } from './yarConfig.ts';

export const SUBSCRIBERS_ONLY = 'فقط فعال برای خریداران اشتراک';

interface ActivationGate {
  readonly active: boolean;
  readonly optedIn: boolean;
  readonly killSwitch: boolean;
  readonly platform?: { readonly gate: string | null; readonly pricingPublished: boolean };
}

export type GateState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly activation: ActivationGate }
  | { readonly kind: 'error' };

/** The server decides (opt-in, kill switches, trial policy, published price). */
function useActivation(slug: string, enabled: boolean): [GateState, () => void] {
  const [state, setState] = useState<GateState>({ kind: 'loading' });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    if (!enabled || slug === '') return;
    let cancelled = false;
    apiGet<ActivationGate>(`/t/${slug}/api/v1/ai/activation`)
      .then((activation) => {
        if (!cancelled) setState({ kind: 'ready', activation });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, enabled, version]);
  return [state, reload];
}

function inactiveText(state: GateState): { readonly title: string; readonly body: string } {
  if (state.kind !== 'ready') {
    return { title: 'وضعیت دستیار', body: 'وضعیت دستیار از سرور خوانده نشد. دوباره تلاش کنید.' };
  }
  const { activation } = state;
  const gate = activation.platform?.gate ?? null;
  if (gate === 'AI_TRIAL_NOT_ALLOWED') {
    return {
      title: SUBSCRIBERS_ONLY,
      body: 'در دورهٔ آزمایشی رابط دستیار را می‌بینید ولی پاسخی داده نمی‌شود. با خرید اشتراک فعال می‌شود.',
    };
  }
  if (gate === 'PLATFORM_AI_DISABLED' || activation.killSwitch) {
    return { title: 'دستیار موقتاً متوقف است', body: 'سرویس دستیار فعلاً پرسش نمی‌پذیرد.' };
  }
  if (!activation.optedIn) {
    return {
      title: 'دستیار برای سازمان فعال نشده است',
      body: 'مدیر سازمان باید از «دستیار هوشمند ← گفتگوها» دستیار را فعال کند.',
    };
  }
  if (activation.platform?.pricingPublished === false) {
    return { title: 'به‌زودی', body: 'قیمت‌گذاری دستیار هنوز منتشر نشده است.' };
  }
  return { title: 'دستیار فعال نیست', body: 'در حال حاضر دستیار برای این سازمان در دسترس نیست.' };
}

export interface ModuleYarState {
  readonly yar: YarDef | null;
  readonly wiring: ModuleYarWiring | undefined;
  readonly slug: string;
  readonly demo: boolean;
  readonly gate: GateState;
  /** True only when the server says this tenant may ask now. */
  readonly active: boolean;
  /** Reads the server activation again (after the admin enables the assistant). */
  readonly reload: () => void;
}

export function useModuleYar(moduleId: string): ModuleYarState {
  const { slug } = useYarAccess();
  const demo = slug === 'demo' || slug === 'demo-preview';
  const wiring = MODULE_YAR_MAP[moduleId];
  const [gate, reload] = useActivation(slug, isAiLive() && !demo && Boolean(wiring));
  const active =
    isAiLive() && !demo && Boolean(wiring) && gate.kind === 'ready' && gate.activation.active;
  return { yar: yarForModule(moduleId), wiring, slug, demo, gate, active, reload };
}

/** What enabling sends and charges; shown next to the admin's enable button. */
const OPT_IN_CONSENT =
  'با فعال‌سازی، پرسش و داده‌های لازمِ همان ماژول برای ساختن پاسخ به سرویس مدل زبانی فرستاده می‌شود. ' +
  'هزینهٔ هر پاسخ بر اساس مصرف واقعی از اعتبار هوش مصنوعی کم می‌شود. پاسخ تا ۱۰۰۰ توکن بی‌درنگ اجرا می‌شود و برای پاسخ بلندتر، سقف هزینه پیش از اجرا از شما تأیید گرفته می‌شود.';

/** Why the character cannot answer here, with the one action that helps. */
export function YarInactiveNotice({ state }: { readonly state: ModuleYarState }) {
  const navigate = useNavigate();
  const capabilities = useCapabilities();
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState<string | null>(null);
  const { demo, gate, slug } = state;
  const base = tenantBasePath(slug);
  const inactive = inactiveText(gate);
  const loading = gate.kind === 'loading' && !demo && isAiLive();
  const notOptedIn = gate.kind === 'ready' && !gate.activation.optedIn;
  // The server enforces ai.settings.manage on PUT /activation; this only picks the button.
  const canEnable = notOptedIn && !demo && capabilities.hasCapability('ai.settings.manage');

  async function enable() {
    setEnabling(true);
    setEnableError(null);
    try {
      await apiPut(`/t/${slug}/api/v1/ai/activation`, { optedIn: true });
      state.reload();
    } catch {
      setEnableError('فعال‌سازی انجام نشد. دوباره تلاش کنید یا از «دستیار هوشمند» فعال کنید.');
    } finally {
      setEnabling(false);
    }
  }

  return (
    <Alert
      tone="warning"
      title={demo || !isAiLive() ? SUBSCRIBERS_ONLY : loading ? 'در حال بررسی…' : inactive.title}
    >
      {demo
        ? 'این فضای کاری نمایشی است؛ رابط دستیار را می‌بینید ولی پاسخی داده نمی‌شود.'
        : !isAiLive()
          ? 'سرویس هوش مصنوعی در این نسخه فعال نیست.'
          : loading
            ? 'وضعیت دستیار از سرور خوانده می‌شود.'
            : canEnable
              ? OPT_IN_CONSENT
              : inactive.body}
      {enableError ? (
        <p role="alert" style={{ margin: '8px 0 0' }}>
          {enableError}
        </p>
      ) : null}
      {loading ? null : (
        <div style={{ marginTop: 10 }}>
          {canEnable ? (
            <V2Button size="sm" variant="primary" disabled={enabling} onClick={() => void enable()}>
              {enabling ? 'در حال فعال‌سازی…' : 'فعال‌سازی دستیار برای سازمان'}
            </V2Button>
          ) : (
            <V2Button
              size="sm"
              variant="primary"
              onClick={() =>
                navigate(
                  demo ? '/start/trial' : notOptedIn ? `${base}/ai` : `${base}/admin/subscription`,
                )
              }
            >
              {demo
                ? 'می‌خواهم ERP خودم رو بسازم'
                : notOptedIn
                  ? 'رفتن به دستیار هوشمند'
                  : 'مشاهدهٔ اشتراک'}
            </V2Button>
          )}
        </div>
      )}
    </Alert>
  );
}
